import { ChartResponsiveAxisTick } from '@modules/charts/components/ChartResponsiveAxisTick';
import { ChartTooltipWrapper } from '@modules/charts/components/ChartTooltipWrapper';
import { ChartTooltipWrapperProps } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipWrapper';
import {
  getDeltaLabelValue,
  getDisplayNameForIncompleteTimeWindow,
  getHoverTooltipMaxWidth,
  getLineChartAxisDomain,
  getPropertyNameForIncompleteTimeWindow,
  getXAxisTickInterval,
  isIncompleteTimeWindowProperty,
} from '@modules/charts/components/LineChartWrapper/helpers';
import {
  GetPropertyNameForIncompleteTimeWindow,
  INCOMPLETE_END_SUFFIX,
  INCOMPLETE_START_SUFFIX,
} from '@modules/charts/components/LineChartWrapper/helpers/getPropertyNameForIncompleteTimeWindow';
import {
  useGetLineChartStrokeColor,
  useTransformLineChartData,
} from '@modules/charts/components/LineChartWrapper/hooks';
import { LineChartDot } from '@modules/charts/components/LineChartWrapper/LineChartDot';
import { LineChartLegend } from '@modules/charts/components/LineChartWrapper/LineChartLegend';
import { LineChartTooltip } from '@modules/charts/components/LineChartWrapper/LineChartTooltip';
import { LineChartXAxisLabel } from '@modules/charts/components/LineChartWrapper/LineChartXAxisLabel';
import { HAS_CATEGORY } from '@modules/charts/constants';
import { LineChartInputData, TooltipPosition } from '@modules/charts/types';
import { GetChartLabelOptions, SortBy } from '@modules/common/types';
import {
  formatNumToStandardNotation,
  getCategoryTypeFromString,
  getDateRangeWindow,
} from '@modules/common/utils';
import { isValidSentiment } from '@modules/common/utils/isValidSentiment';
import { Box, Grid, Theme } from '@mui/material';
import { useTheme } from '@mui/system';
import { EqlField, SentimentType } from 'generated/graphql';
import { useState } from 'react';
import { useMeasure } from 'react-use';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AxisDomain, BaseAxisProps, Margin } from 'recharts/types/util/types';
import { AggregatePeriod, TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

interface LineChartWrapperProps<T> {
  baseWhereClause: WhereClause;
  dateRange: TimeRange;
  aggregatePeriod: AggregatePeriod;
  data: T[];
  timestamps?: number[];
  fieldName?: FieldNames;
  qbField?: string;
  displayName?: string;
  xAxis: {
    labelData: {
      primary: BaseAxisProps['label'];
      secondary?: string;
    };
    dataKey: string;
    tickFontSize?: string;
    size?: number;
    tickCount?: number;
    onPrevClick?: () => void;
    onNextClick?: () => void;
    isNextDisabled?: boolean;
    shouldShowTimeRangeControls?: boolean;
  };
  yAxis: {
    label: BaseAxisProps['label'];
    charLength?: number;
    tickFontSize?: string;
    size?: number;
    tickCount?: number;
    domain?: AxisDomain;
  };
  margin?: Margin;
  maxHeight?: number;
  minHeight?: number;
  shouldShowLineDots?: boolean;
  shouldShowAddToFilter?: boolean;
  shouldShowActionTooltip?: boolean;
  shouldShowInfoTooltip?: boolean;
  shouldShowLegends?: boolean;
  shouldUsePrimaryStrokeColor?: boolean;
  shouldShowCartesianGrid?: boolean;
  shouldShowDeltaRefLines?: boolean;
  shouldShowAxisLine?: boolean;
  shouldBreakTickText?: boolean;
  shouldShowStartEndTicks?: boolean;
  delta?: {
    value: number;
    label: string;
    startTimestamp: number | undefined;
    endTimestamp: number | undefined;
  };
  shouldCalculateTickDomain?: boolean;
  sortBy?: SortBy;
  shouldShowVerticalLegendsInNewRow?: boolean;
  getLabelOptions?: GetChartLabelOptions;
  shouldAutoAdjustHeight?: boolean; // Parent component should have a fixed height for this to work
  shouldShowLegendsInNewRow?: boolean;
  shouldDisableAnimation?: boolean;
  onMergeComplete?: () => void;
  shouldExcludeDataPointWhereClause?: boolean;
  formulaID: EQLFunctionName;
  refetch?: () => Promise<void[]>;
  measuredAsCriteria: EqlField | null;
}

export function LineChartWrapper<T extends LineChartInputData>({
  data,
  timestamps = [],
  xAxis,
  yAxis,
  baseWhereClause = EmptyWhereClause,
  aggregatePeriod,
  fieldName,
  qbField,
  displayName,
  dateRange,
  margin = { top: 10, left: 5, right: 20, bottom: 10 },
  maxHeight = 350,
  minHeight = 350,
  shouldShowAddToFilter = true,
  shouldShowActionTooltip = true,
  shouldShowInfoTooltip = true,
  shouldShowLegends = true,
  shouldShowCartesianGrid = true,
  shouldUsePrimaryStrokeColor = false,
  shouldShowDeltaRefLines = false,
  shouldShowAxisLine = false,
  shouldShowLineDots = true,
  shouldBreakTickText = true,
  shouldCalculateTickDomain = false,
  shouldShowStartEndTicks = false,
  delta,
  sortBy,
  formulaID,
  shouldShowLegendsInNewRow = false,
  shouldDisableAnimation = false,
  getLabelOptions,
  onMergeComplete,
  shouldExcludeDataPointWhereClause = false,
  refetch,
  shouldAutoAdjustHeight = false,
  measuredAsCriteria,
}: LineChartWrapperProps<T>) {
  const theme: Theme = useTheme();

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipData, setTooltipData] = useState<
    ChartTooltipWrapperProps['payload'] | null
  >(null);
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>(null);

  const [containerRef, { width: containerWidth }] = useMeasure();

  const {
    lineChartData,
    labelsMap,
    labelsList,
    hasIncompleteTimeWindow,
    yMax,
  } = useTransformLineChartData({
    data,
    timestamps,
    aggregatePeriod,
    dateRange,
    sortBy,
  });

  const getStrokeColor = useGetLineChartStrokeColor({
    fieldName,
    shouldUsePrimaryStrokeColor,
  });

  const renderLines = (
    incompleteStartEndSuffix?: GetPropertyNameForIncompleteTimeWindow['incompleteStartEndSuffix'],
  ) => {
    return labelsList.map((label, index) => {
      const strokeColor = getStrokeColor(index, label);
      const lineDisplayName = getPropertyNameForIncompleteTimeWindow({
        displayName: label.name,
        incompleteStartEndSuffix,
      });

      return (
        <Line
          isAnimationActive={!shouldDisableAnimation}
          key={`${label.id}_${lineDisplayName}`}
          animationDuration={500}
          type="monotoneX"
          dataKey={(data) => data?.[lineDisplayName]?.value}
          name={lineDisplayName}
          stroke={strokeColor}
          strokeDasharray={incompleteStartEndSuffix ? '6 8' : undefined}
          strokeWidth={2}
          dot={
            shouldShowLineDots ? (
              <LineChartDot
                isIncompleteLine={!!incompleteStartEndSuffix}
                dataPointsCount={lineChartData.length}
                anomalyFill={theme.palette.error.main}
              />
            ) : (
              false
            )
          }
          activeDot={
            shouldShowActionTooltip ? (
              <LineChartDot
                isIncompleteLine={!!incompleteStartEndSuffix}
                dataPointsCount={lineChartData.length}
                isOnHoverVariant
                name={lineDisplayName}
                anomalyFill={theme.palette.error.main}
                handleMouseEnter={(eve, dataKey, value, payload) => {
                  const dataLabel =
                    getDisplayNameForIncompleteTimeWindow(dataKey ?? '') ?? '';
                  const labelsMapValue = labelsMap.get(dataLabel ?? '');
                  const payloadValue = payload?.[dataLabel];

                  setShowTooltip(true);
                  setTooltipData({
                    id: labelsMapValue?.id ?? '',
                    value: value ?? 0,
                    label: dataLabel,
                    timestamp: payload?.timestamp,
                    entitySentiment:
                      fieldName === FieldNames.Entity &&
                      labelsMapValue?.id &&
                      isValidSentiment(labelsMapValue.id)
                        ? (labelsMapValue.id as SentimentType)
                        : undefined,
                    color: strokeColor,
                    anomalyValue:
                      typeof payloadValue === 'object'
                        ? payloadValue?.anomalyValue
                        : undefined,
                    category:
                      getCategoryTypeFromString(
                        dataLabel.split(HAS_CATEGORY)[1] ?? '',
                      ) ?? undefined,
                  });
                  setTooltipPosition({
                    clientX: eve.clientX,
                    clientY: eve.clientY,
                  });
                }}
                handleMouseLeave={() => {
                  setShowTooltip(false);
                }}
              />
            ) : undefined
          }
        />
      );
    });
  };

  const handleMouseEnterOnLegend = (event: React.MouseEvent, index: number) => {
    if (!shouldShowActionTooltip) {
      return;
    }
    setShowTooltip(true);
    const dataItem = labelsList[index];
    setTooltipData({
      id: dataItem.id,
      label: dataItem.name,
      value: dataItem.value ?? 0,
      category: dataItem.category,
      color: getStrokeColor(index, dataItem),
    });
    setTooltipPosition({
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const handleMouseLeaveOnLegend = () => {
    if (!shouldShowActionTooltip) {
      return;
    }
    setShowTooltip(false);
  };

  const tooltipDateRange = tooltipData?.timestamp
    ? getDateRangeWindow({
        timestamp: tooltipData.timestamp,
        aggregatePeriod,
      })
    : dateRange;

  const yAxisCharLength = yAxis.charLength || 14;

  const deltaLabel = delta?.label ?? '';
  const deltaValue = delta?.value ?? 0;

  const deltaRefLinesColor =
    deltaValue > 0
      ? theme.palette.success.main
      : deltaValue < 0
      ? theme.palette.error.main
      : theme.palette.warning.main;

  const firstDeltaDataPointIndex = lineChartData.findIndex(
    (dataPoint) => dataPoint.timestamp === delta?.startTimestamp,
  );
  const lastDeltaDataPointIndex = lineChartData.findIndex(
    (dataPoint) => dataPoint.timestamp === delta?.endTimestamp,
  );

  const firstDataPointIndex =
    firstDeltaDataPointIndex >= 0 ? firstDeltaDataPointIndex : 0;
  const lastDataPointIndex =
    lastDeltaDataPointIndex >= 0
      ? lastDeltaDataPointIndex
      : lineChartData.length - 1;

  const firstDataPoint = lineChartData[firstDataPointIndex];
  const lastDataPoint = lineChartData[lastDataPointIndex];

  const xAxisTicks =
    shouldShowStartEndTicks && firstDataPoint && lastDataPoint
      ? [firstDataPoint.dateRange, lastDataPoint.dateRange]
      : undefined;

  const anomalyDataPointIndex = lineChartData.findIndex(
    (dataPoint) => dataPoint.isAnomalous,
  );
  const anomalyDataPoint = lineChartData[anomalyDataPointIndex];

  const size =
    containerWidth < 550
      ? 'extra-small'
      : containerWidth < 750
      ? 'small'
      : 'regular';

  const isSmallSize = size === 'small' || size === 'extra-small';

  const yAxisDomain =
    yAxis.domain ?? getLineChartAxisDomain({ shouldCalculateTickDomain, yMax });

  const chartColumns = shouldShowLegendsInNewRow ? 12 : 10;
  const legendColumns = shouldShowLegendsInNewRow ? 12 : 2;

  const xAxisPrimaryLabel = xAxis.labelData.primary;

  const containerHeight =
    isSmallSize && shouldShowLegends ? 'calc(100% - 30px)' : '100%';

  return (
    <Box ref={containerRef} height="100%" data-role="trend-chart-wrapper">
      <ChartTooltipWrapper
        payload={tooltipData}
        showTooltip={showTooltip}
        tooltipPosition={tooltipPosition}
        setShowTooltip={setShowTooltip}
        setTooltipPosition={setTooltipPosition}
        baseWhereClause={baseWhereClause}
        fieldName={fieldName}
        qbField={qbField}
        displayName={displayName}
        dateRange={tooltipDateRange}
        shouldShowAddToFilter={shouldShowAddToFilter}
        getLabelOptions={getLabelOptions}
        formulaID={formulaID}
        onMergeComplete={onMergeComplete}
        shouldExcludeDataPointWhereClause={shouldExcludeDataPointWhereClause}
        onFeedbackListDrawerClose={refetch}
        measuredAsCriteria={measuredAsCriteria}
      />
      <Grid
        container
        spacing={1}
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        height="100%"
      >
        <Grid
          item
          height={shouldAutoAdjustHeight ? containerHeight : '100%'}
          xs={!isSmallSize && shouldShowLegends ? chartColumns : 12}
        >
          <ResponsiveContainer
            height={shouldAutoAdjustHeight ? '100%' : undefined}
            minHeight={shouldAutoAdjustHeight ? undefined : minHeight}
            width="99%"
            key={isSmallSize.toString()}
          >
            <LineChart
              data={lineChartData}
              margin={margin}
              style={{
                height: 'auto',
              }}
            >
              {shouldShowCartesianGrid && (
                <CartesianGrid
                  stroke={theme.palette.text.secondary}
                  vertical={false}
                  strokeDasharray="3 3"
                />
              )}
              <XAxis
                type="category"
                dataKey={xAxis.dataKey}
                tickLine={false}
                allowDecimals={false}
                height={xAxis.size ?? 48}
                tickSize={0}
                ticks={xAxisTicks}
                interval={getXAxisTickInterval(
                  lineChartData.length,
                  isSmallSize,
                  xAxis.tickCount,
                )}
                tickMargin={15}
                axisLine={shouldShowAxisLine}
                label={
                  typeof xAxisPrimaryLabel === 'string' ? (
                    <LineChartXAxisLabel
                      shouldShowTimeRangeControls={
                        xAxis.shouldShowTimeRangeControls
                      }
                      onPrevClick={xAxis.onPrevClick}
                      onNextClick={xAxis.onNextClick}
                      labelData={{
                        primary: xAxisPrimaryLabel,
                        secondary: xAxis.labelData.secondary,
                      }}
                      isNextDisabled={xAxis.isNextDisabled}
                      aggregatePeriod={aggregatePeriod}
                    />
                  ) : (
                    xAxisPrimaryLabel
                  )
                }
                tick={
                  <ChartResponsiveAxisTick
                    textAnchor="middle"
                    shouldDisableTooltip
                    shouldAdjustFontSize
                    shouldTruncate={false}
                    shouldBreakTickText={shouldBreakTickText}
                    shouldAdjustAnchorForStartEnd={shouldShowStartEndTicks}
                    fontSize={xAxis.tickFontSize}
                    ticks={{
                      startIndex: firstDataPointIndex,
                      endIndex: lastDataPointIndex,
                      totalCount: lineChartData.length,
                    }}
                    highlightTick={
                      anomalyDataPointIndex !== undefined
                        ? {
                            index: anomalyDataPointIndex,
                            color: theme.palette.error.light,
                          }
                        : undefined
                    }
                    tickFormatter={formatNumToStandardNotation}
                  />
                }
              />
              <YAxis
                type="number"
                axisLine={shouldShowAxisLine}
                tickLine={false}
                domain={yAxisDomain}
                interval={shouldShowStartEndTicks ? 'preserveStartEnd' : 0}
                width={yAxis.size ?? yAxisCharLength * 6.8}
                tick={
                  <ChartResponsiveAxisTick
                    charLength={yAxisCharLength}
                    fontSize={yAxis.tickFontSize}
                  />
                }
                tickCount={yAxis.tickCount}
                tickSize={0}
                tickMargin={!isSmallSize ? 10 : 0}
                allowDecimals={false}
                label={
                  !!yAxis.label
                    ? {
                        value: yAxis.label,
                        position: 'left',
                        fill: theme.palette.text.primary,
                        angle: -90,
                        offset: -20,
                        style: {
                          textAnchor: 'middle',
                          fontSize: theme.typography.subtitle1.fontSize,
                        },
                      }
                    : ''
                }
              />
              {shouldShowInfoTooltip && (
                <RechartsTooltip
                  position={
                    isSmallSize && labelsList.length > 10
                      ? { y: -20 }
                      : undefined
                  }
                  wrapperStyle={{
                    zIndex: 9999,
                    maxHeight: isSmallSize ? 'calc(100% + 100px)' : undefined,
                    overflow: isSmallSize ? 'hidden' : undefined,
                  }}
                  content={
                    !showTooltip ? (
                      <LineChartTooltip
                        filter={(payload) =>
                          payload.filter(
                            (p) => !isIncompleteTimeWindowProperty(p.name),
                          )
                        }
                        maxWidth={getHoverTooltipMaxWidth(size)}
                        getLabelOptions={getLabelOptions}
                        formulaID={formulaID}
                      />
                    ) : (
                      <></>
                    )
                  }
                />
              )}
              {renderLines()}
              {hasIncompleteTimeWindow && renderLines(INCOMPLETE_START_SUFFIX)}
              {hasIncompleteTimeWindow && renderLines(INCOMPLETE_END_SUFFIX)}
              {shouldShowDeltaRefLines && firstDataPoint && lastDataPoint && (
                <>
                  <ReferenceLine
                    x={firstDataPoint.dateRange}
                    stroke={deltaRefLinesColor}
                    isFront
                    strokeWidth={2}
                    ifOverflow="extendDomain"
                  />
                  <ReferenceDot
                    x={firstDataPoint.dateRange}
                    y={Number(
                      getDeltaLabelValue(firstDataPoint, deltaLabel) ?? 0,
                    )}
                    r={5}
                    fill={deltaRefLinesColor}
                    stroke="none"
                    ifOverflow="extendDomain"
                  />
                  <ReferenceLine
                    x={lastDataPoint.dateRange}
                    stroke={deltaRefLinesColor}
                    isFront
                    strokeWidth={2}
                    ifOverflow="extendDomain"
                  />
                  <ReferenceDot
                    x={lastDataPoint.dateRange}
                    y={Number(
                      getDeltaLabelValue(lastDataPoint, deltaLabel) ?? 0,
                    )}
                    r={5}
                    fill={deltaRefLinesColor}
                    stroke="none"
                    ifOverflow="extendDomain"
                  />
                </>
              )}
              {anomalyDataPoint && (
                <ReferenceLine
                  x={anomalyDataPoint.dateRange}
                  stroke={theme.palette.error.main}
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                  strokeDasharray="8 8"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Grid>
        {shouldShowLegends && (
          <Grid
            item
            xs={!isSmallSize ? legendColumns : 12}
            maxHeight={
              shouldAutoAdjustHeight || shouldShowLegendsInNewRow
                ? '100%'
                : minHeight
            }
            sx={
              shouldShowLegendsInNewRow
                ? {
                    margin: 2,
                  }
                : null
            }
            overflow="auto"
          >
            <LineChartLegend
              labels={labelsList}
              fieldName={fieldName}
              isSmallSize={isSmallSize}
              getColor={getStrokeColor}
              getLabelOptions={getLabelOptions}
              shouldShowLegendsInNewRow={shouldShowLegendsInNewRow}
              onMouseEnter={handleMouseEnterOnLegend}
              onMouseLeave={handleMouseLeaveOnLegend}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
